/**
 * @fileoverview OneClickBlur Component - Quick Blur Action Buttons
 *
 * This component provides the "Single Click Blur" and "Continue" buttons
 * on the Upload step, allowing users to either quickly blur all faces
 * or proceed to the Configure step for more control.
 *
 * @description
 * Key features:
 * - Single Click Blur: Detects all faces, applies high blur, and downloads
 * - Continue: Proceeds to Configure step for manual control
 * - Two box layout when files uploaded: files info box + action buttons box
 * - Single box layout when no files: just action buttons box
 * - Consistent box sizing with UploadStep drop zone
 *
 * @requires React
 */
import React from 'react';

/**
 * OneClickBlur Component
 *
 * @param {Object} props
 * @param {Array} props.files - Array of uploaded files
 * @param {Function} props.onClearAll - Callback to clear all files
 * @param {boolean} props.canProceed - Whether the buttons should be enabled
 * @param {Function} props.onContinue - Callback for Continue button
 * @param {Function} props.onOneClickBlur - Callback for Single Click Blur button
 * @param {Function} props.onShowToast - Callback to show toast notifications
 * @returns {JSX.Element}
 */
function OneClickBlur({ files, onClearAll, canProceed, onContinue, onOneClickBlur, onShowToast }) {
  const hasFiles = files && files.length > 0;

  // Common box styling to match UploadStep drop zone
  const boxClass = "bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10";

  return (
    <div className="max-w-3xl mx-auto mt-4 space-y-4">
      {/* Box 1: Selected Files Info + Thumbnails (only shown when files uploaded) */}
      {hasFiles && (
        <div className={boxClass}>
          <div className="flex justify-center items-center gap-4 mb-4">
            <span className="text-gray-300 font-medium">Selected ({files.length})</span>
            <span className="text-gray-600">|</span>
            <button
              onClick={onClearAll}
              className="text-red-400 hover:text-red-300 transition-colors font-medium"
            >
              Clear All
            </button>
          </div>

          {/* Centered File Thumbnails */}
          <div className="flex justify-center flex-wrap gap-3">
            {files.map((file, i) => (
              <div key={i} className="bg-white/5 rounded-lg p-2 relative">
                {file.isVideo ? (
                  <div className="relative">
                    <img
                      src={file.thumbnail || file.preview}
                      alt=""
                      className="w-20 h-20 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-8 bg-black/50 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ) : (
                  <img src={file.preview} alt="" className="w-20 h-20 object-cover rounded-lg" />
                )}
                <p className="text-xs text-gray-400 mt-1 truncate max-w-[80px] text-center">{file.name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Box 2: Action Buttons (always shown) */}
      <div className={boxClass}>
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
          {/* Single Click Blur Option */}
          <button
            onClick={canProceed ? onOneClickBlur : () => onShowToast?.('Please upload first.', 'error')}
            className={`px-6 py-3 bg-gradient-to-r ${canProceed ? 'from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600' : 'from-gray-600 to-gray-600 opacity-60 cursor-not-allowed'} rounded-xl font-medium transition-colors flex flex-col items-center gap-1`}
            title="Detect all faces and download blurred images instantly"
          >
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-base">Single Click Blur</span>
            </div>
            <span className="text-xs text-purple-200 opacity-80">
              Detect, Blur faces, Instant Download
            </span>
          </button>

          {/* Divider */}
          <span className="text-gray-500 font-medium text-sm px-2">OR</span>

          {/* Continue Option */}
          <button
            onClick={canProceed ? onContinue : () => onShowToast?.('Please upload first.', 'error')}
            className={`px-6 py-3 bg-gradient-to-r ${canProceed ? 'from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600' : 'from-gray-600 to-gray-600 opacity-60 cursor-not-allowed'} rounded-xl font-medium transition-colors flex flex-col items-center gap-1`}
            title="Proceed to configure blur settings manually with custom BlurBox option."
          >
            <div className="flex items-center gap-2">
              <span className="text-base">Continue</span>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
            <span className="text-xs text-emerald-200 opacity-80">
              Detect → Configure → Preview → Download
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default OneClickBlur;
